import { useState, useEffect, useCallback } from 'react';
import { 
  ClipboardCheck, Search, RefreshCw, Package, Eye, CheckCircle2, 
  AlertTriangle, XCircle, FileText, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateOnly, formatDate } from '@/utils/dateFormat';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { grnV2API } from '@/services/api';
import GRNDetailView from '@/components/grn/GRNDetailView';

const GRNVerificationPage = () => {
  const [pendingGRNs, setPendingGRNs] = useState([]);
  const [completedGRNs, setCompletedGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTab, setSelectedTab] = useState('pending');
  
  // Verification dialog
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [verifyItems, setVerifyItems] = useState([]);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewGRN, setViewGRN] = useState(null);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const warehouseId = user?.entity_id;

  const fetchGRNs = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      // Fetch pending verification GRNs using the V2 combined endpoint
      const pendingRes = await grnV2API.listPendingVerification();
      setPendingGRNs(pendingRes.data || []);
      
      // Fetch completed GRNs from both collections
      const completedLocal = await grnV2API.listLocal({ status: 'completed' });
      const completedIntl = await grnV2API.listInternational({ status: 'completed' });
      const allCompleted = [
        ...(completedLocal.data || []).map(g => ({ ...g, grn_type: 'local' })),
        ...(completedIntl.data || []).map(g => ({ ...g, grn_type: 'international' }))
      ].sort((a, b) => new Date(b.verified_at || b.updated_at) - new Date(a.verified_at || a.updated_at));
      setCompletedGRNs(allCompleted);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch GRNs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, toast]);

  useEffect(() => {
    fetchGRNs();
  }, [fetchGRNs]);

  const openVerifyDialog = async (grn) => {
    setSelectedGRN(grn);
    setVerificationNotes('');
    // Initialize verify items with current values
    setVerifyItems(grn.items.map(item => ({
      ...item,
      verified_qty: item.accepted_qty || item.received_qty || 0,
      dispute_reason: ''
    })));
    setVerifyDialogOpen(true);
  };

  const openViewDialog = (grn) => {
    setViewGRN(grn);
    setViewDialogOpen(true);
  };

  const handleVerifyQtyChange = (index, value) => {
    const updated = [...verifyItems];
    const maxQty = updated[index].received_qty || 0;
    const newQty = Math.min(Math.max(0, parseFloat(value) || 0), maxQty);
    updated[index].verified_qty = newQty;
    // Auto-calculate rejected qty
    updated[index].rejected_qty = maxQty - newQty;
    setVerifyItems(updated);
  };

  const handleDisputeReasonChange = (index, value) => {
    const updated = [...verifyItems];
    updated[index].dispute_reason = value;
    setVerifyItems(updated);
  };

  const handleCompleteVerification = async () => {
    // Validate that at least one item has qty > 0
    const hasValidItems = verifyItems.some(item => item.verified_qty > 0);
    if (!hasValidItems) {
      toast({ 
        title: 'Validation Error', 
        description: 'At least one item must have accepted quantity greater than 0', 
        variant: 'destructive' 
      });
      return;
    }

    // A qty change vs what HQ sent needs a reason - it is logged as a dispute
    const missingReason = verifyItems.find(
      item => item.verified_qty !== (item.accepted_qty ?? item.received_qty) && !item.dispute_reason?.trim()
    );
    if (missingReason) {
      toast({
        title: 'Reason required',
        description: `Enter a dispute reason for ${missingReason.product_name} - the accepted qty differs from what HQ sent`,
        variant: 'destructive'
      });
      return;
    }
    
    // Check for items with 0 qty and warn
    const zeroQtyItems = verifyItems.filter(item => item.verified_qty === 0);
    if (zeroQtyItems.length > 0 && zeroQtyItems.length < verifyItems.length) {
      const proceed = window.confirm(
        `${zeroQtyItems.length} item(s) will be marked as rejected (0 accepted qty). Continue?`
      );
      if (!proceed) return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        items: verifyItems.map(i => ({
          item_id: i.item_id,
          product_id: i.product_id,
          accepted_qty: i.verified_qty,
          dispute_reason: i.dispute_reason?.trim() || null
        })),
        notes: verificationNotes?.trim() || null
      };
      
      const isLocal = selectedGRN.grn_type === 'local' || !selectedGRN.grn_type;
      if (isLocal) {
        await grnV2API.verifyLocal(selectedGRN.id, payload);
      } else {
        await grnV2API.verifyInternational(selectedGRN.id, payload);
      }
      
      const disputed = verifyItems.filter(i => i.verified_qty !== (i.accepted_qty ?? i.received_qty)).length;
      toast({
        title: 'Success',
        description: disputed
          ? `GRN verified and stock updated. ${disputed} disputed line(s) logged for HQ.`
          : 'GRN verified and stock updated.'
      });
      setVerifyDialogOpen(false);
      fetchGRNs();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to complete verification', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge className="bg-gray-100 text-gray-800">Draft</Badge>;
      case 'pending_verification':
        return <Badge className="bg-amber-100 text-amber-800">Pending Verification</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredPendingGRNs = pendingGRNs.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.grn_number?.toLowerCase().includes(s) || 
           g.vendor_name?.toLowerCase().includes(s) ||
           g.po_number?.toLowerCase().includes(s);
  });

  const filteredCompletedGRNs = completedGRNs.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.grn_number?.toLowerCase().includes(s) || 
           g.vendor_name?.toLowerCase().includes(s) ||
           g.po_number?.toLowerCase().includes(s);
  });

  // Calculate totals for verify dialog
  const verifyTotalAccepted = verifyItems.reduce((sum, i) => sum + (i.verified_qty || 0), 0);
  const verifyTotalRejected = verifyItems.reduce((sum, i) => sum + ((i.received_qty || 0) - (i.verified_qty || 0)), 0);
  const verifyTotalValue = verifyItems.reduce((sum, i) => {
    const qty = i.verified_qty || 0;
    const price = i.unit_price || 0;
    const tax = (qty * price) * ((i.tax_rate || 0) / 100);
    return sum + (qty * price) + tax;
  }, 0);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="grn-verification-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">GRN Verification</h1>
          <p className="text-gray-500">{user?.entity_name} - Verify and approve incoming goods</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchGRNs} className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Verification</p>
                <p className="text-2xl font-bold text-amber-600">{pendingGRNs.length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Verified Today</p>
                <p className="text-2xl font-bold text-green-600">
                  {completedGRNs.filter(g => {
                    const today = new Date().toISOString().split('T')[0];
                    return g.updated_at?.startsWith(today);
                  }).length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Completed</p>
                <p className="text-2xl font-bold text-teal-600">{completedGRNs.length}</p>
              </div>
              <FileText className="w-8 h-8 text-teal-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-white border-gray-200 shadow-sm mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by GRN number, PO number, or vendor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-50 border-gray-300"
              data-testid="grn-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="mb-4 bg-gray-100">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">
            Pending ({filteredPendingGRNs.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            History ({filteredCompletedGRNs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-amber-500" />
                Pending GRN Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : filteredPendingGRNs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-300" />
                  <p className="text-lg font-medium">All Caught Up!</p>
                  <p className="text-sm mt-1">No pending GRNs to verify</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50">
                        <TableHead className="text-gray-600">GRN #</TableHead>
                        <TableHead className="text-gray-600">PO #</TableHead>
                        <TableHead className="text-gray-600">Vendor</TableHead>
                        <TableHead className="text-gray-600">Receipt Date</TableHead>
                        <TableHead className="text-gray-600 text-center">Items</TableHead>
                        <TableHead className="text-gray-600 text-right">Value</TableHead>
                        <TableHead className="text-gray-600">Status</TableHead>
                        <TableHead className="text-gray-600 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPendingGRNs.map((grn) => (
                        <TableRow key={grn.id} className="border-gray-200 hover:bg-gray-50">
                          <TableCell className="font-medium text-gray-800">{grn.grn_number}</TableCell>
                          <TableCell className="text-teal-600">{grn.po_number || '-'}</TableCell>
                          <TableCell className="text-gray-600">{grn.vendor_name || grn.vendor_snapshot?.vendor_name || '-'}</TableCell>
                          <TableCell className="text-gray-600">{formatDateOnly(grn.receipt_date || grn.received_date || grn.created_at)}</TableCell>
                          <TableCell className="text-center font-medium">{grn.items?.length || 0}</TableCell>
                          <TableCell className="text-right font-medium text-gray-800">
                            ₹{Math.round(Number(grn.grand_total || 0)).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell>{getStatusBadge(grn.status)}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openViewDialog(grn)}
                                className="text-gray-500 hover:text-gray-700"
                                title="View"
                                data-testid={`wh-grn-view-btn-${grn.grn_number}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openVerifyDialog(grn)}
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                                data-testid={`verify-btn-${grn.id}`}
                              >
                                <ClipboardCheck className="w-4 h-4 mr-1" /> Verify
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-500" />
                Verified GRN History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : filteredCompletedGRNs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No History</p>
                  <p className="text-sm mt-1">Completed GRNs will appear here</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-green-50">
                        <TableHead className="text-gray-600">GRN #</TableHead>
                        <TableHead className="text-gray-600">PO #</TableHead>
                        <TableHead className="text-gray-600">Vendor</TableHead>
                        <TableHead className="text-gray-600">GRN Date</TableHead>
                        <TableHead className="text-gray-600">Verified</TableHead>
                        <TableHead className="text-gray-600 text-center">Items</TableHead>
                        <TableHead className="text-gray-600 text-right">Value</TableHead>
                        <TableHead className="text-gray-600">Status</TableHead>
                        <TableHead className="text-gray-600 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCompletedGRNs.slice(0, 50).map((grn) => (
                        <TableRow key={grn.id} className="border-gray-200 hover:bg-gray-50">
                          <TableCell className="font-medium text-gray-800">{grn.grn_number}</TableCell>
                          <TableCell className="text-teal-600">{grn.po_number || '-'}</TableCell>
                          <TableCell className="text-gray-600">{grn.vendor_name || grn.vendor_snapshot?.vendor_name || '-'}</TableCell>
                          <TableCell className="text-gray-600">{formatDateOnly(grn.received_date || grn.receipt_date || grn.created_at)}</TableCell>
                          <TableCell className="text-gray-600">{formatDate(grn.verified_at || grn.updated_at)}</TableCell>
                          <TableCell className="text-center font-medium">{grn.items?.length || 0}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            ₹{Math.round(Number(grn.grand_total || 0)).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell>{getStatusBadge(grn.status)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewDialog(grn)}
                              className="text-gray-500 hover:text-gray-700"
                              title="View"
                              data-testid={`wh-grn-view-btn-${grn.grn_number}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Verify Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-500" />
              Verify GRN: {selectedGRN?.grn_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedGRN && (
            <div className="space-y-4">
              {/* GRN Info */}
              <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">PO Number</p>
                  <p className="font-medium text-teal-600">{selectedGRN.po_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Vendor</p>
                  <p className="font-medium text-gray-800">{selectedGRN.vendor_name || selectedGRN.vendor_snapshot?.vendor_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Receipt Date</p>
                  <p className="font-medium text-gray-800">{formatDateOnly(selectedGRN.received_date || selectedGRN.receipt_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Invoice #</p>
                  <p className="font-medium text-gray-800">{selectedGRN.invoice_number || '-'}</p>
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-gray-600">Product</TableHead>
                      <TableHead className="text-gray-600 text-center">Ordered</TableHead>
                      <TableHead className="text-gray-600 text-center">Received</TableHead>
                      <TableHead className="text-gray-600 text-center">Accept Qty</TableHead>
                      <TableHead className="text-gray-600 text-center">Reject</TableHead>
                      <TableHead className="text-gray-600">Dispute Reason</TableHead>
                      <TableHead className="text-gray-600 text-right">Unit Price</TableHead>
                      <TableHead className="text-gray-600 text-right">Line Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {verifyItems.map((item, index) => (
                      <TableRow key={index} className="border-gray-200">
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-teal-600">{item.sku || item.product_sku}</p>
                          {(item.lot_batch_number || item.batch_number) && (
                            <Badge variant="outline" className="text-xs mt-1">{item.lot_batch_number || item.batch_number}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-gray-500">
                          {item.ordered_qty || '-'} {item.uom}
                        </TableCell>
                        <TableCell className="text-center font-medium text-blue-600">
                          {item.received_qty} {item.uom}
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            max={item.received_qty}
                            value={item.verified_qty}
                            onChange={(e) => handleVerifyQtyChange(index, e.target.value)}
                            className="w-20 text-center bg-gray-50 border-gray-300"
                            data-testid={`verify-qty-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-medium ${item.rejected_qty > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {item.rejected_qty || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="text"
                            placeholder={item.verified_qty !== (item.accepted_qty ?? item.received_qty) ? 'Reason required' : 'Optional'}
                            value={item.dispute_reason || ''}
                            onChange={(e) => handleDisputeReasonChange(index, e.target.value)}
                            className={`w-44 bg-gray-50 ${item.verified_qty !== (item.accepted_qty ?? item.received_qty) && !item.dispute_reason ? 'border-amber-400' : 'border-gray-300'}`}
                            data-testid={`dispute-reason-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-right text-gray-600">
                          ₹{(item.unit_price ?? item.unit_price_inr ?? 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-gray-800">
                          ₹{((item.verified_qty || 0) * (item.unit_price || item.unit_price_inr || 0)).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="bg-teal-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-teal-600">Total Accepted</p>
                    <p className="text-lg font-bold text-teal-700">{verifyTotalAccepted} units</p>
                  </div>
                  <div>
                    <p className="text-red-600">Total Rejected</p>
                    <p className="text-lg font-bold text-red-700">{verifyTotalRejected} units</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Accepted Value</p>
                    <p className="text-lg font-bold text-gray-800">₹{verifyTotalValue.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Verification note */}
              <div>
                <p className="text-sm text-gray-600 mb-1">Verification note (optional)</p>
                <Input
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Any remark for HQ about this receipt"
                  className="bg-gray-50 border-gray-300"
                  data-testid="verification-notes-input"
                />
              </div>

              {/* Warning for 0 qty */}
              {verifyTotalAccepted === 0 && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Cannot Complete</p>
                    <p className="text-sm text-red-700">At least one item must have accepted quantity greater than 0</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteVerification}
              disabled={submitting || verifyTotalAccepted === 0}
              className="bg-green-500 hover:bg-green-600 text-white"
              data-testid="complete-verification-btn"
            >
              {submitting ? 'Processing...' : 'Approve & Update Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Goods Receipt Note</DialogTitle>
            <DialogDescription className="text-gray-500">
              Full receipt detail, pricing, approval history and adjustments for {viewGRN?.grn_number}
            </DialogDescription>
          </DialogHeader>

          <GRNDetailView grn={viewGRN} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GRNVerificationPage;
